import { get } from 'lodash';
import { normalizeMergeTagKey } from './mergeTagDictionary';

export const DYNAMIC_TEXT_FORMAT_HELPER_NAME = 'formatDynamicText';

export type DynamicTextFormat =
	| 'string'
	| 'number'
	| 'decimal'
	| 'dollar'
	| 'dollarCents'
	| 'phoneNumber'
	| 'uppercase'
	| 'lowercase'
	| 'titlecase';

export interface ParsedDynamicTextHelperExpression {
	key: string;
	format?: string;
	maxLength: number | null;
}

export interface DynamicTextFormatOption {
	value: DynamicTextFormat;
	label: string;
}

export const DYNAMIC_TEXT_FORMAT_OPTIONS: DynamicTextFormatOption[] = [
	{ value: 'string', label: 'Text' },
	{ value: 'number', label: 'Number' },
	{ value: 'decimal', label: 'Decimal' },
	{ value: 'dollar', label: 'Dollar' },
	{ value: 'dollarCents', label: 'Dollar (cents)' },
	{ value: 'phoneNumber', label: 'Phone number' },
];

function parseNumericValue(value: unknown): number | null {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	const normalized = String(value ?? '')
		.trim()
		.replace(/[^0-9.-]/g, '');

	if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') {
		return null;
	}

	const parsed = Number(normalized);
	return Number.isFinite(parsed) ? parsed : null;
}

function formatPhoneNumber(value: unknown) {
	const cleaned = String(value ?? '').replace(/\D/g, '');
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	const intlCode = match?.[1] ? `+${match[1]} ` : '';

	if (match) {
		return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
	}

	return String(value ?? '');
}

export function normalizeDynamicTextMaxLength(value: unknown): number | null {
	if (value === null || value === undefined || value === '') return null;

	const parsed = Number(value);
	if (!Number.isFinite(parsed)) return null;

	const normalized = Math.floor(parsed);
	return normalized > 0 ? normalized : null;
}

export function truncateDynamicTextValue(
	value: unknown,
	maxLength: number | null,
): string {
	const content = String(value ?? '');
	if (!maxLength || maxLength < 1) return content;

	// Avoid truncating HTML fragments to prevent broken tags in rich text content.
	if (/<\/?[a-z][\s\S]*>/i.test(content)) {
		return content;
	}

	return content.length > maxLength ? content.slice(0, maxLength) : content;
}

export function applyDynamicTextFormat(value: unknown, format?: string): string {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	const selectedFormat = String(format || 'string');

	switch (selectedFormat) {
		case 'number': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return Math.trunc(numericValue).toLocaleString('en-US', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			});
		}

		case 'decimal': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return numericValue.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		}

		case 'dollar': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return numericValue.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			});
		}

		case 'dollarCents': {
			const numericValue = parseNumericValue(value);
			if (numericValue === null) return String(value);
			return numericValue.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		}

		case 'phoneNumber':
			return formatPhoneNumber(value);

		case 'uppercase':
			return String(value).toUpperCase();

		case 'lowercase':
			return String(value).toLowerCase();

		case 'titlecase':
			return String(value)
				.toLowerCase()
				.split(' ')
				.map(part => part.charAt(0).toUpperCase() + part.slice(1))
				.join(' ');

		default:
			return String(value);
	}
}

export function formatDynamicTextValue(
	value: unknown,
	format?: string,
	maxLength?: number | null,
): string {
	return truncateDynamicTextValue(
		applyDynamicTextFormat(value, format),
		normalizeDynamicTextMaxLength(maxLength),
	);
}

export function parseDynamicTextHelperExpression(
	rawExpression: string,
): ParsedDynamicTextHelperExpression | null {
	const expression = String(rawExpression || '')
		.replace(/&quot;/gi, '"')
		.replace(/&#39;|&apos;/gi, "'")
		.trim();
	if (!expression.startsWith(`${DYNAMIC_TEXT_FORMAT_HELPER_NAME} `)) return null;

	const helperMatch = expression.match(/^formatDynamicText\s+([^\s]+)([\s\S]*)$/);
	if (!helperMatch) return null;

	const key = String(helperMatch[1] || '').trim();
	if (!key) return null;

	const rest = String(helperMatch[2] || '');
	const formatMatch = rest.match(/\bformat=(?:"([^"]*)"|'([^']*)'|([^\s]+))/);
	const rawFormat = formatMatch?.[1] ?? formatMatch?.[2] ?? formatMatch?.[3] ?? '';

	const maxLengthMatch = rest.match(/\bmaxLength=(?:"([^"]*)"|'([^']*)'|([^\s]+))/);
	const rawMaxLength =
		maxLengthMatch?.[1] ?? maxLengthMatch?.[2] ?? maxLengthMatch?.[3] ?? '';

	return {
		key,
		format: rawFormat ? String(rawFormat).trim() : undefined,
		maxLength: normalizeDynamicTextMaxLength(rawMaxLength),
	};
}

export function serializeDynamicTextHelperExpression(
	tagKey: string,
	format?: string,
	maxLength?: number | null,
): string {
	const normalizedKey = String(tagKey || '').trim();
	if (!normalizedKey) return '';

	const normalizedMaxLength = normalizeDynamicTextMaxLength(maxLength);
	const normalizedFormat = String(format || '').trim();
	const useHelper =
		(normalizedFormat && normalizedFormat !== 'string') || normalizedMaxLength !== null;

	if (!useHelper) {
		return `{{${normalizedKey}}}`;
	}

	const helperArgs = [`${DYNAMIC_TEXT_FORMAT_HELPER_NAME} ${normalizedKey}`];
	if (normalizedFormat && normalizedFormat !== 'string') {
		helperArgs.push(`format="${normalizedFormat}"`);
	}
	if (normalizedMaxLength !== null) {
		helperArgs.push(`maxLength=${normalizedMaxLength}`);
	}

	return `{{${helperArgs.join(' ')}}}`;
}

export function resolveMergeTagValue(
	dataSource: Record<string, any>,
	key: string,
): unknown {
	const rawKey = String(key || '');
	const trimmedKey = rawKey.trim();
	const normalizedRawKey = normalizeMergeTagKey(rawKey);
	const normalizedTrimmedKey = normalizeMergeTagKey(trimmedKey);

	return (
		get(dataSource, rawKey) ??
		get(dataSource, trimmedKey) ??
		get(dataSource, normalizedRawKey) ??
		get(dataSource, normalizedTrimmedKey)
	);
}

export function registerDynamicTextHandlebarsHelpers(Handlebars: any) {
	if (!Handlebars || typeof Handlebars.registerHelper !== 'function') return;

	if (!Handlebars.helpers?.[DYNAMIC_TEXT_FORMAT_HELPER_NAME]) {
		Handlebars.registerHelper(
			DYNAMIC_TEXT_FORMAT_HELPER_NAME,
			(value: unknown, options?: { hash?: Record<string, unknown> }) => {
				const format =
					typeof options?.hash?.format === 'string'
						? String(options?.hash?.format)
						: undefined;
				const maxLength = normalizeDynamicTextMaxLength(options?.hash?.maxLength);
				return formatDynamicTextValue(value, format, maxLength);
			},
		);
	}
}
